import Layout from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initPosthog } from "@/lib/posthog";
import { initSentry } from "@/lib/sentry";
import { createInertiaApp } from "@inertiajs/react";
import Axios from "axios";

import { createElement, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "~/index.css";

Axios.defaults.xsrfHeaderName = "X-CSRF-TOKEN";
Axios.defaults.withCredentials = true;

// Temporary type definition, until @inertiajs/react provides one
// type ResolvedComponent = {
//   default: ReactNode
//   layout?: (page: ReactNode) => ReactNode
// }

initSentry();
initPosthog({ capture_pageview: false });

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.netlify.app/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - Teeba` : "Teeba"),

  // Enable progress bar for better UX
  // see https://inertia-rails.netlify.app/guide/progress-indicators
  progress: {
    delay: 250,
    color: "#3b82f6",
    includeCSS: true,
    showSpinner: true,
  },

  resolve: (name) => {
    const pages = import.meta.glob("../pages/**/*.tsx", {
      eager: true,
    });

    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    if ((window as any).MiniProfiler !== undefined) {
      (window as any).MiniProfiler.pageTransition();
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.netlify.app/guide/pages#default-layouts
    //
    (page as any).default.layout ||= (page: ReactNode) => createElement(Layout, null, page);

    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      // Wrap the app with ErrorBoundary for global error handling
      const AppWithErrorBoundary = () =>
        createElement(ErrorBoundary, {
          onError: (error, errorInfo) => {
            // Integration with Sentry for error reporting
            if ((window as any).Sentry) {
              (window as any).Sentry.withScope((scope: any) => {
                scope.setExtra("errorInfo", errorInfo);
                scope.setTag("errorBoundary", "inertia-app");
                (window as any).Sentry.captureException(error);
              });
            }
          },
          children: createElement(App, props),
        });

      createRoot(el).render(createElement(AppWithErrorBoundary));
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
